import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LocalizedNavLink from '../../hooks/LocalizedNavLink';
import { useTranslation } from 'react-i18next';
import FAQsList from './FAQsList';
import FAQsSkeleton from './FAQsSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './FAQs.scss';

const FAQs = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { data: dynamicData, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/dynamic.json');

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const faqsData = useMemo(() => {
    if (!dynamicData) return null;

    const pageRawData = dynamicData[page];
    const allFAQs = dynamicData.faqs;

    if (!pageRawData || !allFAQs) return null;

    const pageData = {};

    if (page === 'faqPage') {
      if (pageRawData.faq__title) {
        pageData.title = pageRawData.faq__title[currentLang] || pageRawData.faq__title.en;
      }
      if (pageRawData.faq__subtitle) {
        pageData.subtitle = pageRawData.faq__subtitle[currentLang] || pageRawData.faq__subtitle.en;
      }
    } else {
      if (pageRawData.title) {
        pageData.title = pageRawData.title[currentLang] || pageRawData.title.en;
      }
      if (pageRawData.accent) {
        pageData.accent = pageRawData.accent[currentLang] || pageRawData.accent.en;
      }
      if (pageRawData.subtitle) {
        pageData.subtitle = pageRawData.subtitle[currentLang] || pageRawData.subtitle.en;
      }
      if (pageRawData.buttonText) {
        pageData.buttonText = pageRawData.buttonText[currentLang] || pageRawData.buttonText.en;
      }
      if (pageRawData.buttonLink) {
        pageData.buttonLink = pageRawData.buttonLink;
      }
    }

    if (page === 'faqPage') {

      if (allFAQs.length > 0) {
        return { pageData, allFAQs };
      }
    } else {

      if (pageData.title) {
        return { pageData, allFAQs };
      }
    }

    return null;
  }, [dynamicData, page, currentLang]);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (faqsData) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, faqsData]);

  if (isLoading && page !== 'faqPage') {
    return <FAQsSkeleton />;
  }

  if (isLoading && page === 'faqPage') {
    return (
      <div className={`home__faqs__section ${page === 'faqPage' ? 'faqs__page__centered' : ''}`}>
        <div className='right__side__faqs__section'>
          <FAQsList faqs={[]} page={page} isLoading={true} />
        </div>
      </div>
    );
  }

  if (hasError || !faqsData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const { pageData: currentPageData, allFAQs: currentAllFAQs } = faqsData;

  return (
    <div className={`home__faqs__section ${page === 'faqPage' ? 'faqs__page__centered' : ''}`}>
      {page !== 'faqPage' && (
        <div className='left__side__faqs__section'>
          <div className='faqs__content'>
            {currentPageData.accent && (
              <AnimatedOnScroll animation="fade-up" delay={0}>
              <p className='faqs__accent'>
                {currentPageData.accent}
              </p>
              </AnimatedOnScroll>
            )}
            {currentPageData.title && (
              <AnimatedOnScroll animation="fade-up" delay={100}>
              <h4 className='faqs__title'>
                {currentPageData.title}
              </h4>
              </AnimatedOnScroll>
            )}
            {currentPageData.subtitle && (
              <AnimatedOnScroll animation="fade-up" delay={200}>
              <h5 className='faqs__subtitle'>
                {currentPageData.subtitle}
              </h5>
              </AnimatedOnScroll>
            )}
            {page === 'homePage' && currentPageData.buttonText && (
              <AnimatedOnScroll animation="fade-up" delay={300}>
              <LocalizedNavLink 
                to={currentPageData.buttonLink || '/'} 
                className='faqs__button'
              >
                <span></span><span></span><span></span><span></span>
                {currentPageData.buttonText}
              </LocalizedNavLink>
              </AnimatedOnScroll>
            )}
          </div>
        </div>
      )}
      <div className='right__side__faqs__section'>
        <FAQsList faqs={currentAllFAQs} page={page} />
      </div>
    </div>
  );
};

export default React.memo(FAQs);